import { useEffect, useMemo, useRef } from 'react'
import mapboxgl, { type Map as MapboxMap } from 'mapbox-gl'
import type { FeatureCollection, LineString, Point } from 'geojson'
import { useTheme } from '../../theme/ThemeContext.js'
import { useBasemapProvider } from '../../hooks/useBasemapProvider.js'
import { TRAIL_COLOR_DRAFT, TRAIL_COLOR_PUBLISHED } from '../../lib/trailColor.js'
import { disableMapboxTelemetry } from '../../lib/mapboxTelemetry.js'
import { BasemapToggle } from '../BasemapToggle.js'
import { buildTiandituStyle } from '../tianditu/tiandituStyle.js'

// DrawingMap — minimal Mapbox map for the hand-draw editor. Renders a polyline
// through `vertices` plus numbered circle markers, and forwards every map
// click as `onAddVertex(lng, lat)`. Centred on Hangzhou by default; subsequent
// vertex additions don't fitBounds (would jitter while drawing). The user can
// pan/zoom freely.
//
// Intentionally simpler than RouteMap.tsx: no theme-aware contours, no
// terrain DEM, no provider toggle. Drawing mode is a single-purpose surface;
// keeping it self-contained avoids dragging in the full route detail map
// machinery and makes the component trivial to mock in tests.

const MAPBOX_TOKEN = (import.meta.env.VITE_MAPBOX_KEY ?? '').trim()
if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN
}

const LINE_SOURCE = 'xr-drawing-line'
const LINE_LAYER = 'xr-drawing-line-layer'
const PREVIEW_SOURCE = 'xr-drawing-preview-line'
const PREVIEW_LAYER = 'xr-drawing-preview-line-layer'
const POINT_SOURCE = 'xr-drawing-points'
const POINT_LAYER = 'xr-drawing-points-circle'
const POINT_LABEL_LAYER = 'xr-drawing-points-label'

export interface DrawingVertex {
  lng: number
  lat: number
}

export interface DrawingMapProps {
  vertices: DrawingVertex[]
  pointVertices?: DrawingVertex[]
  previewVertices?: DrawingVertex[]
  routedLine?: Array<{ lng: number; lat: number }>
  lineMode?: boolean
  onAddVertex: (lng: number, lat: number) => void
  onBeginMoveVertex?: (index: number) => void
  onMoveVertex?: (index: number, lng: number, lat: number) => void
  onBeginMovePointVertex?: (index: number) => void
  onMovePointVertex?: (index: number, lng: number, lat: number) => void
  onEndMovePointVertex?: () => void
  onEndMoveVertex?: () => void
  onInsertVertex?: (index: number, lng: number, lat: number) => void
  disabled?: boolean
  /** Initial map centre; used only on first render. */
  initialCenter?: [number, number]
  initialZoom?: number
}

export function DrawingMap({
  vertices,
  pointVertices = [],
  previewVertices = [],
  routedLine = [],
  lineMode = true,
  onAddVertex,
  onBeginMoveVertex,
  onMoveVertex,
  onBeginMovePointVertex,
  onMovePointVertex,
  onEndMovePointVertex,
  onEndMoveVertex,
  onInsertVertex,
  disabled = false,
  initialCenter = [120.155, 30.275],
  initialZoom = 11,
}: DrawingMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MapboxMap | null>(null)
  const styleReadyRef = useRef(false)
  const onAddRef = useRef(onAddVertex)
  const onMoveRef = useRef(onMoveVertex)
  const onMovePointRef = useRef(onMovePointVertex)
  const onBeginMoveRef = useRef(onBeginMoveVertex)
  const onBeginMovePointRef = useRef(onBeginMovePointVertex)
  const onEndMoveRef = useRef(onEndMoveVertex)
  const onEndMovePointRef = useRef(onEndMovePointVertex)
  const onInsertRef = useRef(onInsertVertex)
  const verticesRef = useRef(vertices)
  const pointVerticesRef = useRef(pointVertices)
  const previewVerticesRef = useRef(previewVertices)
  const routedLineRef = useRef(routedLine)
  const lineModeRef = useRef(lineMode)
  const disabledRef = useRef(disabled)

  useEffect(() => {
    onAddRef.current = onAddVertex
    onBeginMoveRef.current = onBeginMoveVertex
    onMoveRef.current = onMoveVertex
    onBeginMovePointRef.current = onBeginMovePointVertex
    onMovePointRef.current = onMovePointVertex
    onEndMoveRef.current = onEndMoveVertex
    onEndMovePointRef.current = onEndMovePointVertex
    onInsertRef.current = onInsertVertex
    verticesRef.current = vertices
    pointVerticesRef.current = pointVertices
    previewVerticesRef.current = previewVertices
    routedLineRef.current = routedLine
    lineModeRef.current = lineMode
    disabledRef.current = disabled
  }, [
    disabled,
    lineMode,
    onAddVertex,
    onBeginMoveVertex,
    onBeginMovePointVertex,
    onEndMoveVertex,
    onEndMovePointVertex,
    onInsertVertex,
    onMovePointVertex,
    onMoveVertex,
    pointVertices,
    previewVertices,
    routedLine,
    vertices,
  ])

  const { effective: effectiveTheme } = useTheme()
  const { provider } = useBasemapProvider()
  const styleSource = useMemo<string | mapboxgl.StyleSpecification>(() => {
    if (provider === 'tianditu') {
      const spec = buildTiandituStyle('standard')
      if (spec) return spec as unknown as mapboxgl.StyleSpecification
    }
    return 'mapbox://styles/mapbox/standard'
  }, [provider])
  const providerRef = useRef(provider)
  providerRef.current = provider

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    if (!MAPBOX_TOKEN && providerRef.current !== 'tianditu') return

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: styleSource,
      center: initialCenter,
      zoom: initialZoom,
      attributionControl: true,
      cooperativeGestures: false,
    })
    mapRef.current = map
    disableMapboxTelemetry(map)

    let suppressNextClick = false
    let dragging: { kind: 'line'; index: number } | { kind: 'poi'; index: number } | null = null
    let layerInteractionsBound = false
    const bindLayerInteractions = () => {
      if (layerInteractionsBound || !map.getLayer(LINE_LAYER) || !map.getLayer(POINT_LAYER)) return
      layerInteractionsBound = true
      map.on('click', LINE_LAYER, (event) => {
        const insert = onInsertRef.current
        if (!insert) return
        if (disabledRef.current) return
        const lngLat = event.lngLat
        if (!lineModeRef.current) return
        if (!lngLat || !Number.isFinite(lngLat.lng) || !Number.isFinite(lngLat.lat)) return
        const insertAt = nearestSegmentInsertIndex(verticesRef.current, lngLat.lng, lngLat.lat)
        if (insertAt !== null) {
          suppressNextClick = true
          insert(insertAt, lngLat.lng, lngLat.lat)
        }
      })
      map.on('mousedown', POINT_LAYER, (event) => {
        if (disabledRef.current) return
        const feature = event.features?.[0]
        const pointKind = feature?.properties?.kind === 'poi' ? 'poi' : 'line'
        const rawIndex =
          pointKind === 'poi' ? feature?.properties?.pointIndex : feature?.properties?.index
        const index = Number(rawIndex) - 1
        if (!Number.isInteger(index) || index < 0) return
        suppressNextClick = true
        dragging = { kind: pointKind, index }
        if (pointKind === 'poi') onBeginMovePointRef.current?.(index)
        else onBeginMoveRef.current?.(index)
        map.getCanvas().style.cursor = 'grabbing'
        event.preventDefault()
      })
    }

    map.on('style.load', () => {
      styleReadyRef.current = true
      ensureLayers(map)
      syncSources(
        map,
        verticesRef.current,
        previewVerticesRef.current,
        pointVerticesRef.current,
        lineModeRef.current,
        routedLineRef.current,
      )
      bindLayerInteractions()
      if (providerRef.current !== 'mapbox') return
      try {
        map.setConfigProperty('basemap', 'lightPreset', effectiveTheme === 'dark' ? 'night' : 'day')
      } catch {
        /* ignore — older SDK or non-standard style */
      }
    })

    map.on('click', (event) => {
      if (disabledRef.current) return
      if (suppressNextClick) {
        suppressNextClick = false
        return
      }
      const { lng, lat } = event.lngLat
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
      onAddRef.current(lng, lat)
    })
    map.on('mousemove', (event) => {
      if (disabledRef.current) return
      if (dragging === null) return
      const { lng, lat } = event.lngLat
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
      if (dragging.kind === 'poi') onMovePointRef.current?.(dragging.index, lng, lat)
      else onMoveRef.current?.(dragging.index, lng, lat)
    })
    map.on('mouseup', () => {
      if (dragging !== null) {
        const ended = dragging.kind
        dragging = null
        if (ended === 'poi') onEndMovePointRef.current?.()
        else onEndMoveRef.current?.()
        map.getCanvas().style.cursor = 'crosshair'
      }
    })

    map.getCanvas().style.cursor = 'crosshair'

    return () => {
      mapRef.current = null
      styleReadyRef.current = false
      try {
        map.remove()
      } catch {
        /* ignore */
      }
    }
    // initial center/zoom are intentionally captured once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    styleReadyRef.current = false
    map.setStyle(styleSource)
  }, [styleSource])

  // Re-apply lightPreset when theme flips.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    if (provider !== 'mapbox') return
    try {
      map.setConfigProperty('basemap', 'lightPreset', effectiveTheme === 'dark' ? 'night' : 'day')
    } catch {
      /* ignore */
    }
  }, [effectiveTheme])

  // Push vertices into the GeoJSON sources whenever they change.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReadyRef.current) return
    syncSources(map, vertices, previewVertices, pointVertices, lineMode, routedLine)
  }, [lineMode, pointVertices, previewVertices, vertices, routedLine])

  return (
    <>
      <div
        ref={containerRef}
        data-testid="drawing-editor-map"
        className="h-full w-full"
        style={{ minHeight: 360 }}
      />
      <div className="pointer-events-auto absolute right-3 top-3 z-10">
        <BasemapToggle />
      </div>
    </>
  )
}

function ensureLayers(map: MapboxMap): void {
  if (!map.getSource(LINE_SOURCE)) {
    map.addSource(LINE_SOURCE, {
      type: 'geojson',
      data: emptyLineCollection(),
    })
  }
  if (!map.getLayer(LINE_LAYER)) {
    map.addLayer({
      id: LINE_LAYER,
      type: 'line',
      source: LINE_SOURCE,
      paint: {
        'line-color': TRAIL_COLOR_PUBLISHED,
        'line-width': 4,
        'line-opacity': 0.9,
      },
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
    })
  }
  if (!map.getSource(PREVIEW_SOURCE)) {
    map.addSource(PREVIEW_SOURCE, {
      type: 'geojson',
      data: emptyLineCollection(),
    })
  }
  if (!map.getLayer(PREVIEW_LAYER)) {
    map.addLayer({
      id: PREVIEW_LAYER,
      type: 'line',
      source: PREVIEW_SOURCE,
      paint: {
        'line-color': TRAIL_COLOR_DRAFT,
        'line-width': 5,
        'line-opacity': 0.85,
        'line-dasharray': [2, 1],
      },
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
    })
  }
  if (!map.getSource(POINT_SOURCE)) {
    map.addSource(POINT_SOURCE, {
      type: 'geojson',
      data: emptyPointCollection(),
    })
  }
  if (!map.getLayer(POINT_LAYER)) {
    map.addLayer({
      id: POINT_LAYER,
      type: 'circle',
      source: POINT_SOURCE,
      paint: {
        'circle-radius': 7,
        'circle-color': TRAIL_COLOR_PUBLISHED,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    })
  }
  if (!map.getLayer(POINT_LABEL_LAYER)) {
    map.addLayer({
      id: POINT_LABEL_LAYER,
      type: 'symbol',
      source: POINT_SOURCE,
      layout: {
        'text-field': ['to-string', ['get', 'index']],
        'text-size': 11,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
      },
    })
  }
}

function syncSources(
  map: MapboxMap,
  vertices: DrawingVertex[],
  previewVertices: DrawingVertex[],
  pointVertices: DrawingVertex[],
  lineMode: boolean,
  routedLine?: Array<{ lng: number; lat: number }>,
): void {
  const lineSource = map.getSource(LINE_SOURCE) as mapboxgl.GeoJSONSource | undefined
  const previewSource = map.getSource(PREVIEW_SOURCE) as mapboxgl.GeoJSONSource | undefined
  const pointSource = map.getSource(POINT_SOURCE) as mapboxgl.GeoJSONSource | undefined

  const lineCoords: Array<[number, number]> =
    routedLine && routedLine.length >= 2
      ? routedLine.map((p) => [p.lng, p.lat])
      : vertices.map((v) => [v.lng, v.lat])

  if (lineSource) {
    lineSource.setData(lineMode ? buildLineCollectionFromCoords(lineCoords) : emptyLineCollection())
  }
  if (previewSource) {
    previewSource.setData(
      lineMode && previewVertices.length > 0
        ? buildLineCollection(previewVertices)
        : emptyLineCollection(),
    )
  }
  if (pointSource) {
    pointSource.setData(buildPointCollection(vertices, pointVertices))
  }
}

function buildLineCollection(vertices: DrawingVertex[]): FeatureCollection<LineString> {
  if (vertices.length < 2) return emptyLineCollection()
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: vertices.map((v) => [v.lng, v.lat]),
        },
        properties: {},
      },
    ],
  }
}

function buildLineCollectionFromCoords(
  coordinates: Array<[number, number]>,
): FeatureCollection<LineString> {
  if (coordinates.length < 2) return emptyLineCollection()
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates,
        },
        properties: {},
      },
    ],
  }
}

function buildPointCollection(
  vertices: DrawingVertex[],
  pointVertices: DrawingVertex[] = [],
): FeatureCollection<Point> {
  const points = [
    ...vertices.map((vertex, index) => ({ vertex, kind: 'line' as const, index })),
    ...pointVertices.map((vertex, index) => ({ vertex, kind: 'poi' as const, index })),
  ]
  return {
    type: 'FeatureCollection',
    features: points.map(({ vertex, kind, index }, idx) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [vertex.lng, vertex.lat] },
      properties: { index: idx + 1, pointIndex: index + 1, kind },
    })),
  }
}

function emptyLineCollection(): FeatureCollection<LineString> {
  return { type: 'FeatureCollection', features: [] }
}

function emptyPointCollection(): FeatureCollection<Point> {
  return { type: 'FeatureCollection', features: [] }
}

function nearestSegmentInsertIndex(
  vertices: DrawingVertex[],
  lng: number,
  lat: number,
): number | null {
  if (vertices.length < 2) return null
  let bestIdx = 1
  let bestDistance = Infinity
  for (let i = 1; i < vertices.length; i += 1) {
    const distance = segmentDistanceSquared({ lng, lat }, vertices[i - 1]!, vertices[i]!)
    if (distance < bestDistance) {
      bestDistance = distance
      bestIdx = i
    }
  }
  return bestIdx
}

function segmentDistanceSquared(point: DrawingVertex, a: DrawingVertex, b: DrawingVertex): number {
  const meanLat = ((a.lat + b.lat) / 2 / 180) * Math.PI
  const ax = a.lng * Math.cos(meanLat)
  const ay = a.lat
  const bx = b.lng * Math.cos(meanLat)
  const by = b.lat
  const px = point.lng * Math.cos(meanLat)
  const py = point.lat
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  const t = len2 > 0 ? Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0
  const projX = ax + dx * t
  const projY = ay + dy * t
  return (projX - px) ** 2 + (projY - py) ** 2
}
